import { Delete, Edit, InfoOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Checkbox, Divider, Grid, IconButton, Stack, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { useEffect, useMemo, useState } from 'react';
import { useGetShopConfig } from '../../../services/core/settings';
import { MILEAGE_UNIT, PDF_VISIBILITY_SECTION_KEYS, PDF_VISIBILITY_TYPES } from '../../../utility/Constant';
import CreateWorkOrderSvg from '../../../utility/Svg/CreateWorkOrderSvg';

/** Heading above the per-document totals list, by tab. */
const TOTALS_SECTION_TITLES = {
    [PDF_VISIBILITY_TYPES.INVOICE]: 'Invoice Totals',
    [PDF_VISIBILITY_TYPES.ESTIMATE]: 'Estimate Totals',
    [PDF_VISIBILITY_TYPES.WORK_ORDER]: 'Work Order Totals',
};
import ServicePackageSvg from '../../../utility/Svg/ServicePackageSvg';
import { CustomTooltip } from '../../../utility/helpers/commonFunction';
import BusinessInfoModal from './BusinessInfoModal';
import ServicePackageModal from './ServicePackageModal';

const InvoiceVisibility = ({ settingVisibility, updateVisibilitySettingMutation }) => {
    console.log('🚀 ~ InvoiceVisibility ~ settingVisibility:', settingVisibility);
    const [visibilityType, setVisibilityType] = useState(PDF_VISIBILITY_TYPES.INVOICE);
    // The settings record's section name for the active tab — see PDF_VISIBILITY_SECTION_KEYS.
    const activeSectionKey = PDF_VISIBILITY_SECTION_KEYS[visibilityType] || visibilityType;
    const [visibility, setVisibility] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [servicePackageModalOpen, setServicePackageModalOpen] = useState(false);
    const [servicePackageMode, setServicePackageMode] = useState('separate');

    const handleApplyServicePackageMode = (mode) => {
        const sectionKey = activeSectionKey;

        // UI -> API normalization
        const apiMode = mode === 'separate' ? 'seperate' : mode;

        setVisibility((prev) => {
            const newState = _cloneDeep(prev);

            _set(newState, `${sectionKey}.customer.servicePackageDisplayMode`, apiMode);

            return newState;
        });

        setServicePackageMode(mode);
        setServicePackageModalOpen(false);
    };

    // Map canonical keys to state paths
    const KEY_TO_PATH = useMemo(
        () => ({
            // PART
            partNumber: (s) => `${s}.part.partNumber`,
            partBrand: (s) => `${s}.part.partBrand`,
            partProvider: (s) => `${s}.part.partProvider`,
            partDescription: (s) => `${s}.part.partDescription`,

            // TYRE
            tyreName: (s) => `${s}.tire.tyreName`,
            tyreBrand: (s) => `${s}.tire.tyreBrand`,
            tyreModel: (s) => `${s}.tire.tyreModel`,
            tyreDescription: (s) => `${s}.tire.tyreDescription`,

            // BUSINESS INFO - Simple Booleans
            shopEmail: (s) => `${s}.businessInfo.shopEmail`,
            shopWebsite: (s) => `${s}.businessInfo.shopWebsite`,
            serviceAdvisor: (s) => `${s}.businessInfo.serviceAdvisor`,
            technician: (s) => `${s}.businessInfo.serviceTechnician`,
            droppedOffDate: (s) => `${s}.businessInfo.droppedOff`,
            completionDate: (s) => `${s}.businessInfo.completionDate`,
            estimationExpiryDate: (s) => `${s}.businessInfo.estimationExpiryDate`,
            workOrderDate: (s) => `${s}.businessInfo.workOrderDate`,
            authorizationDate: (s) => `${s}.businessInfo.authorizationDate`,

            // BUSINESS INFO - Objects (visibility property)
            gstHstNumber: (s) => `${s}.businessInfo.gstOrHstNumber.visibility`,
            ein: (s) => `${s}.businessInfo.ein.visibility`,

            // VEHICLE
            mileageUnit: (s) => `${s}.vehicle.mileageUnit`,
            vin: (s) => `${s}.vehicle.vin`,
            licenseNumber: (s) => `${s}.vehicle.licenseNumber`,
            fleetUnitNumber: (s) => `${s}.vehicle.fleetUnitNumber`,

            // CUSTOMER (API keys: customerAddress, packages)
            customerAddress: (s) => `${s}.customer.customerAddress`,
            packages: (s) => `${s}.customer.packages`,
            payments: (s) => `${s}.customer.payments`,
            taxSummary: (s) => `${s}.customer.taxSummary`,
        }),
        []
    );

    // Column configuration for rendering - dynamically changes based on visibilityType
    const getBusinessInfoItems = () => {
        const sectionKey = activeSectionKey;
        const baseItems = [];

        baseItems.push({ label: 'Email', key: 'shopEmail', type: 'boolean' });
        baseItems.push({ label: 'Website', key: 'shopWebsite', type: 'boolean' });

        const einData = _get(visibility, `${sectionKey}.businessInfo.ein`);
        const gstData = _get(visibility, `${sectionKey}.businessInfo.gstOrHstNumber`);

        // Shop info order: business number (GST/HST) first, then EIN when present, then staff and dates
        if (gstData) {
            const gstLabel = _get(gstData, 'label', 'GST/HST #');
            baseItems.push({ label: gstLabel, key: 'gstHstNumber', type: 'object' });
        }

        if (einData) {
            const einLabel = _get(einData, 'label', 'EIN');
            baseItems.push({ label: einLabel, key: 'ein', type: 'object' });
        }

        baseItems.push({ label: 'Service advisor', key: 'serviceAdvisor', type: 'boolean' });

        if (visibilityType === PDF_VISIBILITY_TYPES.INVOICE) {
            baseItems.push({ label: 'Technician', key: 'technician', type: 'boolean' });
        }

        if (visibilityType === PDF_VISIBILITY_TYPES.INVOICE) {
            baseItems.push({ label: 'Dropped off date', key: 'droppedOffDate', type: 'boolean' });
            baseItems.push({ label: 'Completion date', key: 'completionDate', type: 'boolean' });
        }

        if (visibilityType === PDF_VISIBILITY_TYPES.ESTIMATE) {
            baseItems.push({ label: 'Completion date', key: 'completionDate', type: 'boolean' });
            baseItems.push({ label: 'Expiration date', key: 'estimationExpiryDate', type: 'boolean' });
        }

        if (visibilityType === PDF_VISIBILITY_TYPES.WORK_ORDER) {
            baseItems.push({ label: 'Work Order date', key: 'workOrderDate', type: 'boolean' });
            baseItems.push({ label: 'Authorization date', key: 'authorizationDate', type: 'boolean' });
        }

        // Add Additional Business Info Items
        const additionalInfo = _get(visibility, `${sectionKey}.businessInfo.additionalBusinessInfo`, []);
        const additionalItems =
            additionalInfo?.length > 0
                ? additionalInfo.map((item, index) => ({
                      label: item.label,
                      key: `additionalBusinessInfo.${index}`,
                      type: 'additional',
                      index: index,
                      visibility: item.visibility,
                      value: item.value || '',
                  }))
                : [];

        return [...baseItems, ...additionalItems];
    };
    const { data: shopConfigData, isLoading: isConfigLoading, error: configError } = useGetShopConfig();
    const mileageUnit = shopConfigData?.config?.mileageUnit.toLowerCase() === MILEAGE_UNIT.MILES ? MILEAGE_UNIT.MILES : MILEAGE_UNIT.KM;
    console.log('CONFIG UNIT:', shopConfigData);

    const customerVehicleSections = [
        // Customer
        {
            items: [{ label: 'Customer Address', key: 'customerAddress' }],
        },

        // Vehicle
        {
            items: [
                { label: `Mileage unit (${mileageUnit})`, key: 'mileageUnit' },
                { label: 'VIN', key: 'vin' },
                { label: 'Licence plate', key: 'licenseNumber' },
                { label: 'Unit #', key: 'fleetUnitNumber' },
            ],
        },
    ];
    const invoiceTotalItems = [
        {
            label: 'Fixed Price Services : ',
            key: 'packages',
            hasFilter: true,
            showDefaultMode: true,
            infoTooltip: 'Choose how fixed price services appear in invoice totals.',
        },
    ];

    if (visibilityType === PDF_VISIBILITY_TYPES.INVOICE) {
        invoiceTotalItems.push({
            label: 'Payment History',
            key: 'payments',
        });
    }

    invoiceTotalItems.push({
        label: 'Tax Details & Summary',
        key: 'taxSummary',
        infoTooltip: 'Show tax status on line items and include a tax summary table below the totals.',
    });

    const invoiceTotalSection = [
        {
            items: invoiceTotalItems,
        },
    ];
    const columns = [
        {
            title: 'Part/Tire',
            sections: [
                {
                    items: [
                        { label: 'Part #', key: 'partNumber' },
                        { label: 'Part Brand', key: 'partBrand' },
                        { label: 'Part Provider', key: 'partProvider' },
                        { label: 'Part Description', key: 'partDescription' },
                    ],
                },
                {
                    items: [
                        { label: 'Tire Name', key: 'tyreName' },
                        { label: 'Tire Brand', key: 'tyreBrand' },
                        { label: 'Tire Model', key: 'tyreModel' },
                        { label: 'Tire Description', key: 'tyreDescription' },
                    ],
                },
            ],
        },

        {
            title: 'Customer/Vehicle',
            sections: customerVehicleSections,
        },

        {
            title: 'Shop Info',
            items: [],
            isBusinessInfo: true,
        },
    ];

    useEffect(() => {
        if (settingVisibility) {
            const newVisibility = {
                invoice: settingVisibility.invoice || {},
                estimate: settingVisibility.estimate || {},
                workOrderAuthorization: settingVisibility.workOrderAuthorization || {},
            };
            setVisibility(newVisibility);

            const sectionKey = activeSectionKey;
            const rawMode = _get(newVisibility, `${sectionKey}.customer.servicePackageDisplayMode`, 'separate');
            // Normalize API typo
            setServicePackageMode(rawMode === 'seperate' ? 'separate' : rawMode);
        }
    }, [settingVisibility, visibilityType]);

    if (!visibility) return <div>Loading...</div>;

    const handleCheckboxChange = (item, isAdditional = false, index = -1) => {
        const sectionKey = activeSectionKey;
        let fullPath;

        if (isAdditional) {
            fullPath = `${sectionKey}.businessInfo.additionalBusinessInfo[${index}].visibility`;
        } else {
            const pathResolver = KEY_TO_PATH[item.key];
            if (!pathResolver) {
                console.warn(`No path resolver found for key: ${item.key}`);
                return;
            }
            fullPath = pathResolver(sectionKey);
        }

        const currentValue = _get(visibility, fullPath, false);

        setVisibility((prev) => {
            const newState = _cloneDeep(prev);
            _set(newState, fullPath, !currentValue);
            return newState;
        });
    };

    const handleAddBusinessInfo = () => {
        setEditingItem({
            mode: 'ADD',
            data: { label: '', value: '' },
            section: activeSectionKey,
        });
        setModalOpen(true);
    };

    const handleEditBusinessInfo = (item) => {
        const sectionKey = activeSectionKey;
        let data;
        let originalKey = item.key;
        let modalTitle = '';

        if (item.type === 'additional') {
            // For additional items, get directly from array
            const additionalInfo = _get(visibility, `${sectionKey}.businessInfo.additionalBusinessInfo`, []);
            data = additionalInfo[item.index] || { label: '', value: '' };
            modalTitle = `Edit ${item.label}`;
        } else {
            // For EIN/GST - map UI key to data key
            const dataKeyMapping = {
                ein: 'ein',
                gstHstNumber: 'gstOrHstNumber',
            };
            const dataKey = dataKeyMapping[item.key];
            data = _get(visibility, `${sectionKey}.businessInfo.${dataKey}`, { label: '', value: '' });
            originalKey = dataKey;
            modalTitle = `Edit ${item.label}`;
        }

        setEditingItem({
            mode: 'EDIT',
            key: originalKey,
            index: item.index,
            type: item.type,
            data: data,
            section: sectionKey,
            modalTitle: modalTitle,
        });
        setModalOpen(true);
    };

    const handleDeleteBusinessInfo = (index) => {
        const sectionKey = activeSectionKey;
        setVisibility((prev) => {
            const newState = _cloneDeep(prev);
            const additionalInfo = _get(newState, `${sectionKey}.businessInfo.additionalBusinessInfo`, []);
            additionalInfo.splice(index, 1);
            _set(newState, `${sectionKey}.businessInfo.additionalBusinessInfo`, additionalInfo);
            return newState;
        });
    };

    const handleSaveModal = (data) => {
        const { section, mode, type, index, key } = editingItem;

        setVisibility((prev) => {
            const newState = _cloneDeep(prev);

            // Ensure businessInfo structure exists
            if (!_get(newState, `${section}.businessInfo`)) {
                _set(newState, `${section}.businessInfo`, {});
            }

            if (mode === 'ADD') {
                // Get or initialize additionalBusinessInfo array
                let additionalInfo = _get(newState, `${section}.businessInfo.additionalBusinessInfo`, []) || [];
                console.log('🚀 ~ handleSaveModal ~ additionalInfo:', additionalInfo);

                additionalInfo.push({
                    label: data.label,
                    value: data.value,
                    visibility: true,
                });

                _set(newState, `${section}.businessInfo.additionalBusinessInfo`, additionalInfo);
            } else {
                // UPDATE
                if (type === 'additional') {
                    // Update both label and value for additional items
                    _set(newState, `${section}.businessInfo.additionalBusinessInfo[${index}].label`, data.label);
                    _set(newState, `${section}.businessInfo.additionalBusinessInfo[${index}].value`, data.value);
                } else {
                    // For EIN / GST - only update label
                    // Ensure the object exists first
                    if (!_get(newState, `${section}.businessInfo.${key}`)) {
                        _set(newState, `${section}.businessInfo.${key}`, {});
                    }
                    _set(newState, `${section}.businessInfo.${key}.label`, data.label);
                }
            }
            return newState;
        });
        setEditingItem(null);
    };

    const handleCancel = () => {
        if (!settingVisibility) return;
        setVisibility({
            invoice: settingVisibility.invoice || {},
            estimate: settingVisibility.estimate || {},
            workOrderAuthorization: settingVisibility.workOrderAuthorization || {},
        });
    };

    const onSubmit = () => {
        const payload = {
            display: {
                invoice: visibility.invoice,
                estimate: visibility.estimate,
                workOrderAuthorization: visibility.workOrderAuthorization,
            },
        };

        updateVisibilitySettingMutation.mutate(payload, {
            onSuccess: () => {
                // This will trigger a re-fetch of the visibility settings
            },
        });
    };

    const renderVisibilityRow = (item) => {
        const sectionKey = activeSectionKey;
        let checked = false;

        if (item.type === 'additional') {
            checked = item.visibility;
        } else {
            const pathResolver = KEY_TO_PATH[item.key];
            if (!pathResolver) {
                return null;
            }
            const fullPath = pathResolver(sectionKey);
            checked = _get(visibility, fullPath, false);
        }

        const rowKey = item.type === 'additional' ? `additional-${item.index}` : item.key;

        return (
            <Box
                key={rowKey}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '36px',
                    width: '100%',
                    mt: item.key === 'taxSummary' ? '8px' : 0,
                }}
            >
                {/* Show Checkbox for normal items */}
                {!item.hasFilter && (
                    <Checkbox
                        checked={!!checked}
                        disableRipple
                        onChange={() => handleCheckboxChange(item, item.type === 'additional', item.index)}
                        sx={{
                            p: 0,
                            mr: 1,

                            '& .MuiSvgIcon-root': {
                                fontSize: 20,
                            },

                            color: '#BDBDBD',

                            '&.Mui-checked': {
                                color: '#006CDD',
                            },
                        }}
                        icon={
                            <span
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '4px',
                                    border: '2px solid #E6E6E6',
                                    backgroundColor: '#fff',
                                    display: 'inline-block',
                                    boxSizing: 'border-box',
                                    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                }}
                            />
                        }
                    />
                )}

                {/* Show Filter Icon only for Service Packages */}

                {item.hasFilter && (
                    <IconButton
                        size="small"
                        onClick={() => setServicePackageModalOpen(true)}
                        sx={{
                            p: '2px',
                            mr: 1,
                        }}
                    >
                        <ServicePackageSvg />
                    </IconButton>
                )}

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'nowrap',
                    }}
                >
                    {/* Label */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Typography
                            sx={{
                                color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                                fontFeatureSettings: "'liga' off, 'clig' off",
                                fontFamily: 'Roboto',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '150%',
                                letterSpacing: '0.15px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.label}
                        </Typography>

                        {item.infoTooltip && !item.showDefaultMode && (
                            <CustomTooltip width="430px" placement="top" title={<Typography fontSize="14px">{item.infoTooltip}</Typography>}>
                                <InfoOutlined sx={{ fontSize: 16, color: '#8C8C8C' }} />
                            </CustomTooltip>
                        )}
                    </Box>

                    {/* Default Mode */}

                    {/* Default Mode */}
                    {item.showDefaultMode && (
                        <Typography
                            sx={{
                                color: '#006CDD',
                                fontFeatureSettings: "'liga' off, 'clig' off",
                                fontFamily: 'Roboto',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '150%',
                                letterSpacing: '0.15px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {' '}
                            {servicePackageMode === 'separate'
                                ? 'Show as Service Packages'
                                : servicePackageMode === 'merge_into_labour'
                                  ? 'Merge into Labour'
                                  : 'Merge into Parts'}
                        </Typography>
                    )}
                </Box>

                {item.infoTooltip && item.showDefaultMode && (
                    <CustomTooltip width="430px" placement="top" title={<Typography fontSize="14px">{item.infoTooltip}</Typography>}>
                        <InfoOutlined
                            sx={{
                                fontSize: 16,
                                color: '#8C8C8C',
                                ml: 0.5,
                            }}
                        />
                    </CustomTooltip>
                )}

                {(item.type === 'object' || item.type === 'additional') && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 0.5,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleEditBusinessInfo(item)}
                            sx={{
                                p: '2px',
                            }}
                        >
                            <Edit
                                sx={{
                                    fontSize: 16,
                                    color: '#646466',
                                }}
                            />
                        </IconButton>

                        {item.type === 'additional' && (
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteBusinessInfo(item.index)}
                                sx={{
                                    p: '2px',
                                }}
                            >
                                <Delete
                                    sx={{
                                        fontSize: 16,
                                        color: '#E53935',
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
        borderRadius: '30px',
        padding: '6px',
        background: '#E9E9EA',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: 'max-content',
        '& .MuiToggleButton-root': {
            border: 'none',
            fontSize: '16px',
            fontWeight: 400,
            padding: '0 16px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            borderRadius: '50px',
            textTransform: 'capitalize',
            height: '40px',
            // Hover must not repaint these chips — the selected one stays blue, the others stay grey.
            '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: '#006CDD',
                color: '#FFF',
            },
            '&:not(.Mui-selected), &:not(.Mui-selected):hover': {
                backgroundColor: '#E9E9EA',
                color: '#000',
            },
        },
    }));

    return (
        <Box>
            <Grid
                container
                sx={{ boxShadow: ' 0 2px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid rgba(0, 0, 0, 0.10)', bgcolor: 'white' }}
                padding="24px"
                borderRadius="10px"
                marginTop="32px"
            >
                <Grid item xs={12} md={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Visibility on PDFs
                    </Typography>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Box
                        sx={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.10)',
                            boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                        }}
                    >
                        <Stack gap={1.5}>
                            {/* Toggle */}
                            <Box display="flex" justifyContent="center">
                                <StyledToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={visibilityType}
                                    onChange={(event, newValue) => {
                                        if (newValue) setVisibilityType(newValue);
                                    }}
                                >
                                    <ToggleButton value={PDF_VISIBILITY_TYPES.INVOICE}>
                                        {visibilityType === PDF_VISIBILITY_TYPES.INVOICE ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                style={{ marginRight: '6px' }}
                                            >
                                                <path
                                                    d="M14.6667 13.75H5.33333V12.25H14.6667V13.75ZM14.6667 10.75H5.33333V9.25H14.6667V10.75ZM14.6667 7.75H5.33333V6.25H14.6667V7.75ZM3 17.5L4.16667 16.375L5.33333 17.5L6.5 16.375L7.66667 17.5L8.83333 16.375L10 17.5L11.1667 16.375L12.3333 17.5L13.5 16.375L14.6667 17.5L15.8333 16.375L17 17.5V2.5L15.8333 3.625L14.6667 2.5L13.5 3.625L12.3333 2.5L11.1667 3.625L10 2.5L8.83333 3.625L7.66667 2.5L6.5 3.625L5.33333 2.5L4.16667 3.625L3 2.5V17.5Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                style={{ marginRight: '6px' }}
                                            >
                                                <path
                                                    d="M14.6667 13.75H5.33333V12.25H14.6667V13.75ZM14.6667 10.75H5.33333V9.25H14.6667V10.75ZM14.6667 7.75H5.33333V6.25H14.6667V7.75ZM3 17.5L4.16667 16.375L5.33333 17.5L6.5 16.375L7.66667 17.5L8.83333 16.375L10 17.5L11.1667 16.375L12.3333 17.5L13.5 16.375L14.6667 17.5L15.8333 16.375L17 17.5V2.5L15.8333 3.625L14.6667 2.5L13.5 3.625L12.3333 2.5L11.1667 3.625L10 2.5L8.83333 3.625L7.66667 2.5L6.5 3.625L5.33333 2.5L4.16667 3.625L3 2.5V17.5Z"
                                                    fill="black"
                                                />
                                            </svg>
                                        )}
                                        Invoice
                                    </ToggleButton>
                                    <ToggleButton value={PDF_VISIBILITY_TYPES.ESTIMATE}>
                                        {visibilityType === PDF_VISIBILITY_TYPES.ESTIMATE ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                style={{ marginRight: '6px' }}
                                            >
                                                <path
                                                    d="M11.6673 1.66602H5.00065C4.08398 1.66602 3.34232 2.41602 3.34232 3.33268L3.33398 16.666C3.33398 17.5827 4.07565 18.3327 4.99232 18.3327H15.0007C15.9173 18.3327 16.6673 17.5827 16.6673 16.666V6.66601L11.6673 1.66602ZM12.5007 9.99935H9.16732V10.8327H11.6673C12.1257 10.8327 12.5007 11.2077 12.5007 11.666V14.166C12.5007 14.6243 12.1257 14.9993 11.6673 14.9993H10.834V15.8327H9.16732V14.9993H7.50065V13.3327H10.834V12.4993H8.33398C7.87565 12.4993 7.50065 12.1243 7.50065 11.666V9.16601C7.50065 8.70768 7.87565 8.33268 8.33398 8.33268H9.16732V7.49935H10.834V8.33268H12.5007V9.99935ZM10.834 6.66601V2.91602L14.584 6.66601H10.834Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                style={{ marginRight: '6px' }}
                                            >
                                                <path
                                                    d="M11.6673 1.66602H5.00065C4.08398 1.66602 3.34232 2.41602 3.34232 3.33268L3.33398 16.666C3.33398 17.5827 4.07565 18.3327 4.99232 18.3327H15.0007C15.9173 18.3327 16.6673 17.5827 16.6673 16.666V6.66601L11.6673 1.66602ZM12.5007 9.99935H9.16732V10.8327H11.6673C12.1257 10.8327 12.5007 11.2077 12.5007 11.666V14.166C12.5007 14.6243 12.1257 14.9993 11.6673 14.9993H10.834V15.8327H9.16732V14.9993H7.50065V13.3327H10.834V12.4993H8.33398C7.87565 12.4993 7.50065 12.1243 7.50065 11.666V9.16601C7.50065 8.70768 7.87565 8.33268 8.33398 8.33268H9.16732V7.49935H10.834V8.33268H12.5007V9.99935ZM10.834 6.66601V2.91602L14.584 6.66601H10.834Z"
                                                    fill="black"
                                                />
                                            </svg>
                                        )}
                                        Estimate
                                    </ToggleButton>
                                    <ToggleButton value={PDF_VISIBILITY_TYPES.WORK_ORDER}>
                                        <span style={{ display: 'flex', marginRight: '6px' }}>
                                            <CreateWorkOrderSvg color={visibilityType === PDF_VISIBILITY_TYPES.WORK_ORDER ? 'white' : 'black'} />
                                        </span>
                                        Work Order
                                    </ToggleButton>
                                </StyledToggleButtonGroup>
                            </Box>

                            {/* Columns: first two shrink to content; Shop Info grows to fill remaining width */}

                            <Grid
                                container
                                spacing={0}
                                sx={{
                                    alignItems: 'flex-start',
                                }}
                            >
                                {columns.map((column, colIndex) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                        md={4}
                                        key={column.title}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                        }}
                                    >
                                        {/* Header */}
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: '#1A1A1A',
                                                background: '#F5F5F5',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: '12px',
                                                mb: 1,
                                                borderRadius: '2px',
                                            }}
                                        >
                                            {column.title}
                                        </Typography>

                                        {/* Shop Info */}
                                        {column.isBusinessInfo ? (
                                            <Stack
                                                gap={0}
                                                sx={{
                                                    px: '8px',
                                                }}
                                            >
                                                {getBusinessInfoItems().map((item) => renderVisibilityRow(item))}
                                            </Stack>
                                        ) : (
                                            <Stack
                                                gap={0}
                                                sx={{
                                                    px: '8px',
                                                }}
                                            >
                                                {column.sections.map((section, secIdx) => (
                                                    <Box key={secIdx}>
                                                        {section.title && (
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: '14px',
                                                                    color: '#1A1A1A',
                                                                    background: '#F5F5F5',
                                                                    height: '28px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    px: '12px',
                                                                    mb: 1,
                                                                    borderRadius: '2px',
                                                                }}
                                                            >
                                                                {section.title}
                                                            </Typography>
                                                        )}

                                                        <Stack gap={0}>{section.items.map((item) => renderVisibilityRow(item))}</Stack>

                                                        {secIdx < column.sections.length - 1 && (
                                                            <Divider
                                                                sx={{
                                                                    my: 1,
                                                                    borderColor: 'rgba(0,0,0,0.08)',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                            <Box
                                sx={{
                                    // px: '8px',
                                    alignItems: 'flex-start',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: '#1A1A1A',
                                        background: '#F5F5F5',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: '12px',
                                        mb: 1,
                                        borderRadius: '2px',
                                    }}
                                >
                                    {TOTALS_SECTION_TITLES[visibilityType] || 'Invoice Totals'}
                                </Typography>

                                <Stack gap={0} sx={{ width: 'fit-content', maxWidth: '100%', px: '8px' }}>
                                    {invoiceTotalSection[0].items.map((item) => renderVisibilityRow(item))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddBusinessInfo}
                        sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            mt: 1.5,
                            height: '38px',
                        }}
                    >
                        ADD SHOP INFO
                    </Button>
                </Grid>

                {/* Action Buttons */}
                <Grid container item xs={12} justifyContent="flex-end">
                    <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
                        <Button variant="outlined" sx={{ textTransform: 'none', minWidth: 120 }} onClick={handleCancel}>
                            CANCEL CHANGES
                        </Button>
                        <Button onClick={onSubmit} variant="contained" sx={{ textTransform: 'none', minWidth: 120 }}>
                            SAVE CHANGES
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <BusinessInfoModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingItem(null);
                }}
                onSave={handleSaveModal}
                initialData={editingItem?.data}
                isValueEditable={editingItem?.mode === 'ADD' || editingItem?.type === 'additional'}
                title={editingItem?.modalTitle || 'Add Shop Info'}
            />
            <ServicePackageModal
                open={servicePackageModalOpen}
                onClose={() => setServicePackageModalOpen(false)}
                value={servicePackageMode}
                onApply={handleApplyServicePackageMode} // onChange prop removed
            />
        </Box>
    );
};

export default InvoiceVisibility;
